import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { TopContractByValue } from "../../../types/contractDashboard";
import styles from "./topContractsByValue.module.scss";

interface TopContractsByValueProps {
  data: TopContractByValue[];
}

const TopContractsByValue: React.FC<TopContractsByValueProps> = ({ data }) => {
  // Memoize the formatted chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    return data.map((contract) => ({
      name: contract.vendor_name,
      value: contract.contract_value_total, // Already in millions
      fullName: contract.vendor_name,
    }));
  }, [data]);

  // Memoize the max value calculation
  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 10;
    const max = Math.max(...chartData.map((item) => item.value));
    return Math.ceil(max * 1.1); // Round up to nearest whole number with 10% padding
  }, [chartData]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.tooltip}>
          <Typography variant="body2" className={styles.tooltipLabel}>
            {payload[0].payload.fullName}
          </Typography>
          <Typography variant="body2" className={styles.tooltipValue}>
            ${payload[0].value.toFixed(2)}M
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom label for bar end
  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    return (
      <text
        x={x + width + 10}
        y={y + height / 2}
        fill="#0b2b26"
        fontSize={13}
        fontWeight={600}
        textAnchor="start"
        dominantBaseline="middle"
      >
        ${value.toFixed(1)}M
      </text>
    );
  };

  // Truncate long vendor names for Y-axis
  const formatYAxis = (value: string) => {
    return value.length > 20 ? `${value.substring(0, 20)}...` : value;
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Top 10 Contracts by Deal Value
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Highest value active contracts in portfolio.
          </Typography>
        </Box>
        <Box className={styles.emptyState}>
          <Typography variant="body2" className={styles.emptyText}>
            No contract data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Top 10 Contracts by Deal Value
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Highest value active contracts in portfolio.
        </Typography>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 80, left: 20, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e0e0e0"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              domain={[0, maxValue]}
              tickFormatter={(value) => Math.round(value).toString()}
              tick={{ fill: "#8ea39a", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
              label={{
                value: "Value ($ Millions)",
                position: "insideBottom",
                offset: -5,
                style: { fill: "#8ea39a", fontSize: 12 },
              }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: "#4a6b63", fontSize: 13 }}
              width={150}
              axisLine={{ stroke: "#e0e0e0" }}
              tickLine={false}
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f5f5f5" }} />
            <Bar
              dataKey="value"
              fill="#00d2aa"
              radius={[0, 4, 4, 0]}
              label={renderCustomLabel}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(TopContractsByValue);
